// App Configuration Constants
export const APP_CONFIG = {
  NAME: 'SegMailer',
  VERSION: '0.1.0',
  DESCRIPTION: 'Secure email management made simple',
} as const;

// API Configuration
export const API_CONFIG = {
  SERVER_URL:
    process.env.REACT_APP_SERVER_URL || 'http://localhost:5000/api/v1',
  SERVER_API_URL:
    process.env.REACT_APP_SERVER_API_URL || 'http://localhost:5000/api/v2',
  GOOGLE_CLIENT_ID:
    process.env.REACT_APP_GOOGLE_CLIENT_ID || 'your-google-client-id-here',
  REDIRECT_URL:
    process.env.REACT_APP_REDIRECT_URL || 'http://localhost:3000/dashboard',
} as const;

// Routes
export const ROUTES = {
  HOME: '/',
  LOGIN: '/',
  DASHBOARD: '/dashboard',
  CAMPAIGNS: '/campaigns',
  CAMPAIGNS_V2: '/campaigns-v2',
  ANALYTICS: '/analytics',
  CONTACTS: '/contacts',
  SETTINGS: '/settings',
  BROADCASTS: '/broadcasts',
  SEGMENTS: '/segments',
  UNSUBSCRIBE_GROUPS: '/unsubscribe-groups',
  DELIVERIES: '/deliveries',
  CONTENT: '/content',
  DESIGN_STUDIO: '/design-studio',
  ACTIVITY_LOGS: '/activity-logs',
  DATA_INTEGRATIONS: '/data-integrations',
  TRANSACTIONAL: '/transactional',
  ANONYMOUS: '/anonymous',
  CUSTOM_OBJECTS: '/custom-objects',
  DOMAIN_SETUP: '/domain-setup',
  API_DOCS: '/api-docs',
} as const;

// Storage Keys
export const STORAGE_KEYS = {
  TOKEN: 'token',
  USER: 'user',
  WORKSPACE: 'workspace',
} as const;

// Theme Colors (synced with index.css CSS variables and workflowNodeHelpers NODE_COLORS)
export const THEME = {
  PRIMARY: '#4a7b9d',
  SECONDARY: '#64748b',
  SUCCESS: '#2d8f6f',
  WARNING: '#c9a227',
  ERROR: '#c44d4d',
  INFO: '#4a8fc4',
  MUTED: '#6b7280',
  BACKGROUND: '#f8fafc',
  CARD_BACKGROUND: '#ffffff',
  BORDER: '#e2e8f0',
  TEXT_PRIMARY: '#1e293b',
  TEXT_SECONDARY: '#64748b',
  TEXT_MUTED: '#94a3b8',
} as const;

// Navigation Item Type
export interface NavigationItem {
  id: string;
  label: string;
  icon: string;
  route: string;
}

// Navigation Group Type
export interface NavigationGroup {
  id: string;
  label: string;
  items: NavigationItem[];
}

// Dashboard Navigation Items (grouped) - commented out in favor of flat list
// export const NAVIGATION_GROUPS: NavigationGroup[] = [
//   {
//     id: 'overview',
//     label: 'Overview',
//     items: [
//       { id: 'dashboard', label: 'Dashboard', icon: 'LayoutDashboard', route: ROUTES.DASHBOARD },
//       { id: 'contacts', label: 'Contacts', icon: 'Users', route: ROUTES.CONTACTS },
//       { id: 'activity-logs', label: 'Activity Logs', icon: 'Activity', route: ROUTES.ACTIVITY_LOGS },
//       { id: 'segments', label: 'Segments', icon: 'Filter', route: ROUTES.SEGMENTS },
//     ],
//   },
//   {
//     id: 'messaging',
//     label: 'Messaging',
//     items: [
//       { id: 'campaigns', label: 'Campaigns', icon: 'Send', route: ROUTES.CAMPAIGNS },
//       { id: 'broadcasts', label: 'Broadcasts', icon: 'Radio', route: ROUTES.BROADCASTS },
//       { id: 'content', label: 'Templates', icon: 'FileText', route: ROUTES.CONTENT },
//     ],
//   },
//   {
//     id: 'insights',
//     label: 'Insights',
//     items: [
//       { id: 'analytics', label: 'Analytics', icon: 'BarChart3', route: ROUTES.ANALYTICS },
//       { id: 'deliveries', label: 'Deliverability', icon: 'Navigation', route: ROUTES.DELIVERIES },
//     ],
//   },
//   {
//     id: 'configuration',
//     label: 'Configuration',
//     items: [
//       { id: 'data-integrations', label: 'Integrations', icon: 'Settings2', route: ROUTES.DATA_INTEGRATIONS },
//       { id: 'domain-setup', label: 'Domain Setup', icon: 'Code', route: ROUTES.DOMAIN_SETUP },
//       { id: 'settings', label: 'Settings', icon: 'Settings', route: ROUTES.SETTINGS },
//       { id: 'unsubscribe-groups', label: 'Unsubscribe Groups', icon: 'Bell', route: ROUTES.UNSUBSCRIBE_GROUPS },
//     ],
//   },
// ];

// Flat navigation list (all items in a single array, no groups)
export const NAVIGATION_ITEMS: NavigationItem[] = [
  {
    id: 'dashboard',
    label: 'Dashboard',
    icon: 'LayoutDashboard',
    route: ROUTES.DASHBOARD,
  },
  { id: 'contacts', label: 'Contacts', icon: 'Users', route: ROUTES.CONTACTS },
  { id: 'segments', label: 'Segments', icon: 'Filter', route: ROUTES.SEGMENTS },
  {
    id: 'campaigns',
    label: 'Campaigns',
    icon: 'Send',
    route: ROUTES.CAMPAIGNS,
  },
  // { id: 'broadcasts', label: 'Broadcasts', icon: 'Radio', route: ROUTES.BROADCASTS },
  // { id: 'content', label: 'Templates', icon: 'FileText', route: ROUTES.CONTENT },
  // { id: 'analytics', label: 'Analytics', icon: 'BarChart3', route: ROUTES.ANALYTICS },
  // { id: 'deliveries', label: 'Deliverability', icon: 'Navigation', route: ROUTES.DELIVERIES },
  // { id: 'data-integrations', label: 'Integrations', icon: 'Settings2', route: ROUTES.DATA_INTEGRATIONS },
  {
    id: 'activity-logs',
    label: 'Activity Logs',
    icon: 'Activity',
    route: ROUTES.ACTIVITY_LOGS,
  },
  {
    id: 'domain-setup',
    label: 'Domain Setup',
    icon: 'Code',
    route: ROUTES.DOMAIN_SETUP,
  },
  {
    id: 'unsubscribe-groups',
    label: 'Unsubscribe Groups',
    icon: 'Bell',
    route: ROUTES.UNSUBSCRIBE_GROUPS,
  },
  {
    id: 'api-docs',
    label: 'API Docs',
    icon: 'Code',
    route: ROUTES.API_DOCS,
  },
  {
    id: 'settings',
    label: 'Settings',
    icon: 'Settings',
    route: ROUTES.SETTINGS,
  },
];

// Breadcrumb config: static labels for routes (detail pages show label, not ID)
// Order matters: more specific patterns must come first
export interface BreadcrumbRoute {
  pattern: string; // e.g. '/contacts/:id' or '/dashboard'
  label: string;
  parentPath?: string; // for building trail, e.g. '/contacts' for '/contacts/:id'
}

export const BREADCRUMB_ROUTES: BreadcrumbRoute[] = [
  // Contacts (most specific first)
  {
    pattern: '/contacts/edit/:id',
    label: 'Edit Contact',
    parentPath: '/contacts',
  },
  { pattern: '/contacts/add', label: 'Add Contact', parentPath: '/contacts' },
  {
    pattern: '/contacts/upload-csv',
    label: 'Upload CSV',
    parentPath: '/contacts',
  },
  {
    pattern: '/contacts/:id',
    label: 'Contact Details',
    parentPath: '/contacts',
  },
  { pattern: '/contacts', label: 'Contacts' },
  // Segments
  {
    pattern: '/segments/smart/edit/:id',
    label: 'Edit Segment',
    parentPath: '/segments',
  },
  {
    pattern: '/segments/smart/:id',
    label: 'Smart Segment Details',
    parentPath: '/segments',
  },
  {
    pattern: '/segments/manual/:id',
    label: 'Manual Segment Details',
    parentPath: '/segments',
  },
  {
    pattern: '/segments/smart',
    label: 'New Smart Segment',
    parentPath: '/segments',
  },
  { pattern: '/segments', label: 'Segments' },
  {
    pattern: '/campaigns/:campaignId/node/:nodeId/email-builder/:templateId',
    label: 'Email Builder',
    parentPath: '/campaigns/builder/:campaignId',
  },
  {
    pattern: '/campaigns/:campaignId/node/:nodeId/email-builder',
    label: 'Email Builder',
    parentPath: '/campaigns/builder/:campaignId',
  },
  {
    pattern: '/campaigns/builder/:id',
    label: 'Campaign Builder',
    parentPath: '/campaigns',
  },
  {
    pattern: '/campaigns/workflow/:id',
    label: 'Workflow',
    parentPath: '/campaigns',
  },
  { pattern: '/campaigns', label: 'Campaigns' },
  {
    pattern: '/domain-setup/:domainId/records',
    label: 'Domain Records',
    parentPath: '/domain-setup',
  },
  { pattern: '/domain-setup', label: 'Domain Setup' },
  { pattern: '/dashboard', label: 'Dashboard' },
  { pattern: '/activity-logs', label: 'Activity Logs' },
  { pattern: '/data-integrations', label: 'Integrations' },
  { pattern: '/settings', label: 'Settings' },
  { pattern: '/workspace-settings', label: 'Workspace Settings' },
  { pattern: '/account-settings', label: 'Account Settings' },
  { pattern: '/unsubscribe-groups', label: 'Unsubscribe Groups' },
  {
    pattern: '/emails/view/:id',
    label: 'Email View',
    parentPath: '/campaigns',
  },
  { pattern: '/analytics', label: 'Analytics' },
  { pattern: '/broadcasts', label: 'Broadcasts' },
  { pattern: '/deliveries', label: 'Deliverability' },
  { pattern: '/content', label: 'Templates' },
  { pattern: '/api-docs', label: 'API Docs' },
];

export interface BreadcrumbItem {
  path: string;
  label: string;
}

/** Convert pattern like '/contacts/:id' or '/path/:optional?' to regex that matches pathname */
function patternToRegex(pattern: string): RegExp {
  const escaped = pattern
    .replace(/:[^/]+\?/g, '(/[^/]+)?') // optional param
    .replace(/:[^/]+/g, '[^/]+');
  return new RegExp(`^${escaped}$`);
}

/** Extract param names from pattern in order (e.g. /a/:id/b/:x -> ['id','x']) */
function getParamNames(pattern: string): string[] {
  const names: string[] = [];
  pattern.replace(/:([^/]+)\??/g, (_, name) => {
    names.push(name);
    return '';
  });
  return names;
}

/** Extract params from pathname using pattern (pattern and pathname must match). Use same param names in parentPath. */
function getParamsFromPath(
  pattern: string,
  pathname: string
): Record<string, string> {
  const names = getParamNames(pattern);
  if (names.length === 0) return {};
  const regexSource = pattern
    .replace(/:[^/]+\?/g, '(?:/([^/]+))?')
    .replace(/:[^/]+/g, '([^/]+)');
  const regex = new RegExp(`^${regexSource}$`);
  const m = pathname.match(regex);
  if (!m) return {};
  const out: Record<string, string> = {};
  names.forEach((name, i) => {
    if (m[i + 1]) out[name] = m[i + 1];
  });
  return out;
}

/** Resolve path template by replacing :param with values from params */
function resolvePath(template: string, params: Record<string, string>): string {
  return template.replace(/:([^/]+)/g, (_, key) => params[key] ?? '');
}

/** Find route that matches this pathname */
function findRouteByPath(pathname: string): BreadcrumbRoute | undefined {
  return BREADCRUMB_ROUTES.find((r) =>
    patternToRegex(r.pattern).test(pathname)
  );
}

/** Get breadcrumb trail for current pathname using static labels (no IDs in labels) */
export function getBreadcrumbs(pathname: string): BreadcrumbItem[] {
  const normalized = pathname.replace(/\/$/, '') || '/';
  const trail: BreadcrumbItem[] = [];

  let currentPath = normalized;
  let match = findRouteByPath(currentPath);
  const matchedLabel = match?.label ?? null;

  // Build parent chain: resolve parentPath (with :param from current path) and walk up
  while (match?.parentPath) {
    const parentPath = match.parentPath;
    const hasParams = /:[^/]+/.test(parentPath);
    const resolvedParentPath = hasParams
      ? resolvePath(parentPath, getParamsFromPath(match.pattern, currentPath))
      : parentPath;
    const parentMatch = findRouteByPath(resolvedParentPath);
    if (!parentMatch) break;
    trail.push({ path: resolvedParentPath, label: parentMatch.label });
    currentPath = resolvedParentPath;
    match = parentMatch;
  }
  trail.reverse(); // root (e.g. Campaigns) first, then leaf parent (e.g. Campaign Builder)

  if (matchedLabel) {
    trail.push({ path: normalized, label: matchedLabel });
  } else {
    trail.push({ path: normalized, label: 'Dashboard' });
  }

  // Home = dashboard; prepend Home, or show only Home when on dashboard
  const home = { path: '/dashboard', label: 'Home' };
  if (currentPath === '/dashboard') {
    return [home];
  }
  return [home, ...trail];
}

// Local Storage Utility
export const LOCAL_STORAGE = {
  set: (key: string, value: any): void => {
    try {
      localStorage.setItem(key, JSON.stringify(value));
    } catch (error) {
      console.error('Error setting localStorage:', error);
    }
  },
  get: <T = any>(key: string): T | null => {
    try {
      const item = localStorage.getItem(key);
      return item ? JSON.parse(item) : null;
    } catch (error) {
      console.error('Error getting localStorage:', error);
      return null;
    }
  },
  remove: (key: string): void => {
    try {
      localStorage.removeItem(key);
    } catch (error) {
      console.error('Error removing localStorage:', error);
    }
  },
  clear: (): void => {
    try {
      localStorage.clear();
    } catch (error) {
      console.error('Error clearing localStorage:', error);
    }
  },
};

export { CAMPAIGN_DATE_PRESETS } from './campaignConstants';
